<template>
  <div class="series-list-item">
    <div class="series-list-item__series-info clickable"
         v-bind:class="{ 'series-list-item__series-info--active': isActive }"
         v-on:click="selectSeries()">
      <div class="series-list-item__series-cover-img-thumb float-left" v-bind:style="coverImageStyle"></div>

      <div class="series-list-item__series-description float-left">
        <div class="series-list-item__series-title typography--body-2 typography--font-weight-500">
          {{ seriesTitle }}
        </div>
      </div>
    </div>

    <transition name="fade">
      <div class="series-list-item__chapter-list" v-if="isActive">
        <div class="text-center mt-16 mb-8" v-if="loading">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled">
            {{ $t('common.loading') }}
          </div>
        </div>

        <div class="pl-8 pr-8" v-else>
          <ChapterListItem
            v-for="chapter in chapters"
            v-bind:key="chapter.id"
            v-bind:chapter="chapter"
            v-bind:is-active="selectedChapterId === chapter.id"
            v-on:select-chapter="selectChapter"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, sortBy, isEmpty } from 'lodash';
  import labels from '@/mixins/labels';
  import ChapterListItem from './ChapterListItem';

  export default {
    name: 'series-list-item',

    components: {
      ChapterListItem,
    },

    props: {
      seriesItem: {
        type: Object,
        default: null,
      },
      isActive: {
        type: Boolean,
        default: false,
      },
      selectedChapterId: {
        type: Number,
        default: null,
      },
    },

    mixins: [
      labels,
    ],

    data() {
      return {
        loading: false,
      };
    },

    computed: {
      ...mapGetters([
        'chaptersBySeriesId',
        'subjectMap',
        'gradeMap',
      ]),

      chapters() {
        if (isNil(this.seriesItem)) {
          return [];
        }

        return sortBy(this.chaptersBySeriesId[this.seriesItem.id], 'position');
      },

      coverImageStyle() {
        if (this.seriesItem && this.seriesItem.cover_image_mini) {
          return {
            'background-image': `url('${this.seriesItem.cover_image_mini}')`,
          };
        }

        return {};
      },

      subjectName() {
        return this.getSubjectLabel(this.seriesItem);
      },

      gradeName() {
        return this.getGradeLabel(this.seriesItem);
      },

      seriesTitle() {
        if (isNil(this.seriesItem)) {
          return '';
        }

        return `${this.gradeName} ${this.subjectName}: ${this.seriesItem.name}`;
      },
    },

    methods: {
      ...mapActions([
        'fetchSeriesChapters',
      ]),

      selectChapter(chapterId) {
        this.$emit('select-chapter', chapterId);
      },

      selectSeries() {
        if (this.isActive) {
          this.$emit('select-series', null);
          return;
        }

        this.loading = true;
        this.$emit('select-series', this.seriesItem.id);
  
        Promise.resolve()
          .then(() => {
            if (isEmpty(this.chapters)) {
              return this.fetchSeriesChapters(this.seriesItem.id);
            }
  
            return true;
          })
          .then(() => {
            this.loading = false;
          });
      },
    },
  };
</script>

<style lang="scss">
  .series-list-item {
    padding-top: 4px;
    padding-bottom: 4px;

    .fade-enter-active {
      transition: opacity .5s;
    }

    .fade-leave-active {
      transition: opacity .1s;
    }

    .fade-enter, .fade-leave-to {
      opacity: 0;
    }

    .series-list-item__series-info {
      position: relative;
      padding: 8px;
      height: 58px;
      border: 1px solid #e5e5e5;
      border-radius: 2px;
      transition: background-color .5s ease, border-color .5s ease;

      &:hover {
        background-color: #f7f7f7;
      }

      &:active {
        background-color: #eeeeee;
      }

      &.series-list-item__series-info--active {
        border-color: #00c896;
      }
    }

    .series-list-item__series-cover-img-thumb {
      width: 40px;
      height: 40px;
      border-radius: 2px;
      background-color: #f7f7f7;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
    }

    .series-list-item__series-description {
      width: calc(100% - 40px);
      padding: 8px;
    }

    .series-list-item__series-title {
      line-height: 24px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1; /* number of lines to show */
      max-height: 24px;    /* fallback */
    }

    .series-list-item__chapter-list {
      padding-top: 4px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/publishers/components/SeriesListItem.vue